// ============================================================================
// BOOK · new colours (RNC) — their number from the PI sheet, their picture from
// the decision email (ORD-BOOK-1f)
// ============================================================================
// The RNC line's PI is loaded in the order corpus (orders_corpus_files /
// orders_corpus_rows): file "26080037-CL9556-…xlsx", rows "CL9556-1 … -5". The
// new colours are the HIGHEST `colors` numbers on that sheet; its other numbers
// are the PI's ordered colours (the R picks, step 2 of bookColours.picksOf).
// No sheet in the corpus → the highest library colours the R picks do not show
// (bookSources), listed in Book check. David's saved choice (colours[key].r) wins.
// The email's new-colour picture: matched to a number by colour words, else in
// order when the counts agree (COLOUR? — not confirmed), else not used.
// ============================================================================

const ITEM_N = /^(.*)-(\d{1,3})$/;

/**
 * files [{ id, file_name }], rows [{ file_id, item, is_option, remark, color_name, frame_color }] →
 * [{ name, items: { ITEM: { n: { n, colour, frame } } } }] — options (is_option, "-M8", "More option") left out;
 * duplicate files ("…(1).xlsx") give each number once.
 */
export function sheetIndex(files, rows) {
  const byId = new Map((files || []).map((f) => [f.id, { name: String(f.file_name || ''), items: {} }]));
  for (const r of rows || []) {
    const f = byId.get(r.file_id);
    if (!f || r.is_option || /more option/i.test(String(r.remark || ''))) continue;
    const m = String(r.item || '').trim().toUpperCase().match(ITEM_N);
    if (!m || /-M\d/i.test(String(r.item))) continue;
    const it = (f.items[m[1]] = f.items[m[1]] || {});
    const n = Number(m[2]);
    if (n > 0 && !it[n]) it[n] = { n, colour: r.color_name || '', frame: r.frame_color || '' };
  }
  return [...byId.values()];
}

/** The numbers of `item` on every sheet whose file name starts with `pi`, ascending, each once. */
export function sheetColours(sheets, pi, item) {
  const p = String(pi || '').trim(), it = String(item || '').trim().toUpperCase();
  if (!p || !it) return [];
  const out = {};
  for (const f of sheets || []) if (f.name.startsWith(p)) for (const c of Object.values(f.items[it] || {})) out[c.n] = out[c.n] || c;
  return Object.values(out).sort((a, b) => a.n - b.n);
}

/**
 * The PI's answer for a repeat card: { fresh: [n] from the sheets (each RNC line its own pi and colors),
 * ordered: [n] the sheets' other numbers, short: how many new colours no sheet answers, words: { n: text } }.
 */
export function rncPlan(model, sheets) {
  const fresh = [], ordered = [], words = {};
  let short = 0;
  for (const l of model.rncLines || []) {
    const cs = sheetColours(sheets, l.pi, l.item || model.item);
    if (!cs.length) { short += l.colors; continue; }
    const top = cs.slice(Math.max(0, cs.length - l.colors));
    for (const c of cs) {
      words[c.n] = `${c.colour} ${c.frame}`;
      if (top.includes(c)) { if (!fresh.includes(c.n)) fresh.push(c.n); } else if (!ordered.includes(c.n)) ordered.push(c.n);
    }
  }
  return { fresh: fresh.sort((a, b) => a - b), ordered: ordered.filter((n) => !fresh.includes(n)).sort((a, b) => a - b), short, words };
}

const wordSet = (s) => new Set(String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').filter((w) => w.length > 2));

/**
 * The decision email's new-colour pictures for the new numbers: { n: { photo, source } }.
 * emails = decision_emails.new_colors [{ desc, sort, image_url }]; nums = the card's new numbers; words = { n: PI colour text }.
 * A colour-word match is EMAIL; with equal counts the rest go in order as emailq (COLOUR?); otherwise words only.
 */
export function emailPictures(emails, nums, words = {}) {
  const list = [...(emails || [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  const out = {}, used = new Set();
  for (const n of nums) {
    const w = wordSet(words[n]);
    const i = list.findIndex((e, k) => !used.has(k) && e.image_url && [...wordSet(e.desc)].some((t) => w.has(t)));
    if (i >= 0) { used.add(i); out[n] = { photo: { url: list[i].image_url, name: list[i].desc || '' }, source: 'email' }; }
  }
  if (list.length === nums.length) {
    nums.forEach((n, k) => {
      if (out[n] || used.has(k) || !list[k] || !list[k].image_url) return;
      used.add(k); out[n] = { photo: { url: list[k].image_url, name: list[k].desc || '' }, source: 'emailq' };
    });
  }
  return out;
}
